import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dids } from '@/api/dids';
import type { DidRow } from '@/api/dids';
import { carriers } from '@/api/carriers';
import { customers } from '@/api/customers';
import { ivrTrees } from '@/api/ivrTrees';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { formatE164 } from './utils';

interface Props {
  did: DidRow;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

// Roles that may see the full customer picker (vs. scoped to their own account)
const FULL_PICKER_ROLES = ['super_admin', 'reseller_owner'];

export function AssignDidDialog({ did, open, onOpenChange }: Props) {
  const user = useCurrentUser();
  const qc = useQueryClient();

  const showCustomerPicker = FULL_PICKER_ROLES.includes(user.role);

  // Pre-populate from the DID's existing assignment
  const [customerId, setCustomerId] = useState(did.customer_id ?? '');
  const [treeId, setTreeId] = useState(did.routing_target_id ?? '');
  const [carrierId, setCarrierId] = useState(did.carrier_id ?? '');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // When the dialog opens reset to current DID state
  useEffect(() => {
    if (open) {
      setCustomerId(did.customer_id ?? '');
      setTreeId(did.routing_target_id ?? '');
      setCarrierId(did.carrier_id ?? '');
      setFieldErrors({});
    }
  }, [open, did.customer_id, did.routing_target_id, did.carrier_id]);

  // Customer list (only for roles with broad scope)
  const { data: customerList } = useQuery({
    queryKey: ['customers', 'picker'],
    queryFn: () => customers.list({ limit: 200 }),
    enabled: showCustomerPicker && open,
  });

  // IVR trees scoped to the selected customer
  const { data: treeList, isFetching: treesFetching } = useQuery({
    queryKey: ['ivrTrees', 'forCustomer', customerId],
    queryFn: () => ivrTrees.list({ customer_id: customerId || undefined, limit: 200 }),
    enabled: open && !!customerId,
  });

  // Carrier list for outbound picker
  const { data: carrierList } = useQuery({
    queryKey: ['carriers', 'picker', 'voice'],
    queryFn: () => carriers.list({ limit: 200 }),
    enabled: open,
  });
  const voiceCarriers = (carrierList?.rows ?? []).filter(
    (c) => (c.kind === 'voice' || c.kind === 'both') && c.active,
  );

  // When the customer changes, clear the tree selection
  function handleCustomerChange(cid: string) {
    setCustomerId(cid);
    setTreeId('');
  }

  const assign = useMutation({
    mutationFn: () =>
      dids.update(did.id, {
        customer_id: customerId || undefined,
        routing_kind: 'ivr',
        routing_target_id: treeId || undefined,
        carrier_id: carrierId || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dids'] });
      toast({ title: 'DID assigned', description: `${formatE164(did.e164)} routed to selected tree.` });
      onOpenChange(false);
    },
  });

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (showCustomerPicker && !customerId) errs.customer = 'Customer is required.';
    if (!treeId) errs.tree = 'IVR tree is required.';
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    assign.mutate();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!assign.isPending) onOpenChange(v);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            Assign DID
            <span className="block text-sm font-normal font-mono text-muted-foreground mt-0.5">
              {formatE164(did.e164)}
            </span>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          {/* Customer picker — super_admin / reseller_owner only */}
          {showCustomerPicker && (
            <div className="space-y-1.5">
              <Label htmlFor="assign-customer">
                Customer <span aria-hidden className="text-red-500">*</span>
              </Label>
              <Select value={customerId} onValueChange={handleCustomerChange}>
                <SelectTrigger id="assign-customer">
                  <SelectValue placeholder="Select a customer…" />
                </SelectTrigger>
                <SelectContent>
                  {(customerList?.rows ?? []).map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {fieldErrors.customer && (
                <p role="alert" className="text-xs text-red-500">{fieldErrors.customer}</p>
              )}
            </div>
          )}

          {/* IVR Tree picker */}
          <div className="space-y-1.5">
            <Label htmlFor="assign-tree">
              IVR Tree <span aria-hidden className="text-red-500">*</span>
            </Label>
            <Select
              value={treeId}
              onValueChange={setTreeId}
              disabled={(!customerId && showCustomerPicker) || treesFetching}
            >
              <SelectTrigger id="assign-tree">
                <SelectValue placeholder={
                  treesFetching
                    ? 'Loading trees…'
                    : !customerId && showCustomerPicker
                      ? 'Select a customer first…'
                      : 'Select a tree…'
                } />
              </SelectTrigger>
              <SelectContent>
                {(treeList?.rows ?? []).length === 0 && (
                  <div className="px-3 py-2 text-sm text-muted-foreground">No trees found.</div>
                )}
                {(treeList?.rows ?? []).map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fieldErrors.tree && (
              <p role="alert" className="text-xs text-red-500">{fieldErrors.tree}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Extension / transfer / voicemail routing coming soon.
            </p>
          </div>

          {/* Outbound carrier picker (optional) */}
          <div className="space-y-1.5">
            <Label htmlFor="assign-carrier">
              Outbound carrier <span className="text-muted-foreground text-xs">(optional)</span>
            </Label>
            <Select
              value={carrierId || '__auto__'}
              onValueChange={(v) => setCarrierId(v === '__auto__' ? '' : v)}
            >
              <SelectTrigger id="assign-carrier">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__auto__">Auto (priority-based)</SelectItem>
                {voiceCarriers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                    <span className="ms-1.5 text-xs text-muted-foreground">({c.provider})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Override which carrier is used for outbound calls from this DID.
            </p>
          </div>

          {assign.isError && (
            <p role="alert" className="text-xs text-red-500">
              {assign.error instanceof Error ? assign.error.message : 'Failed to assign DID.'}
            </p>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={assign.isPending}>
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={assign.isPending}>
              {assign.isPending ? 'Assigning…' : 'Assign DID'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
